import React from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";
import { useBookingCounts } from "../lib/BookingCountContext";
import {
  Home,
  Search,
  Info,
  UserRound,
  Bell,
  Activity,
  Clock,
  Map,
  Calendar,
} from "lucide-react";

export default function MobileTabBar() {
  const { user } = useAuth();
  const loc = useLocation();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const currentTab = params.get("tab") || "incoming";
  const { pending, active } = useBookingCounts();

  const isOnWorker = user?.role === "worker" && loc.pathname === "/worker";

  let allTabs;

  if (isOnWorker) {
    allTabs = [
      { to: "/worker", label: "Incoming", icon: Bell, badge: pending, matchCheck: () => loc.pathname === "/worker" && currentTab === "incoming" },
      { to: "/worker?tab=active", label: "Active", icon: Activity, badge: active, matchCheck: () => loc.pathname === "/worker" && currentTab === "active" },
      { to: "/worker?tab=past", label: "History", icon: Clock, matchCheck: () => loc.pathname === "/worker" && currentTab === "past" },
      { to: "/worker?tab=map", label: "Map", icon: Map, matchCheck: () => loc.pathname === "/worker" && currentTab === "map" },
      { to: "/worker?tab=schedule", label: "Schedule", icon: Calendar, matchCheck: () => loc.pathname === "/worker" && currentTab === "schedule" },
    ];
  } else {
    const baseTabs = [
      { to: "/", label: t("nav.home"), icon: Home, match: ["/"] },
      { to: "/services", label: t("nav.services"), icon: Search, match: ["/services"] },
      { to: "/about", label: t("nav.about"), icon: Info, match: ["/about", "/contact"] },
    ];

    const profileTab = user
      ? {
          to: user.role === "admin" ? "/admin" : user.role === "worker" ? "/worker" : "/dashboard",
          label: t("nav.profile"),
          icon: UserRound,
          matchPrefix: ["/admin", "/worker", "/dashboard", "/book"],
        }
      : { to: "/login", label: t("nav.profile"), icon: UserRound, match: ["/login", "/signup"] };

    allTabs = [...baseTabs, profileTab];
  }

  const isActive = (t) => {
    if (t.matchCheck) return t.matchCheck();
    if (t.match) return t.match.includes(loc.pathname);
    if (t.matchPrefix) return t.matchPrefix.some(p => loc.pathname.startsWith(p));
    return false;
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#E2E0D9] bg-[#FAF9F6]/95 backdrop-blur-xl safe-area-bottom md:hidden">
      <div className="mx-auto flex max-w-lg items-center justify-around px-2 py-1">
        {allTabs.map((t) => {
          const active = isActive(t);
          const Icon = t.icon;
          return (
            <Link
              key={t.to}
              to={t.to}
              className={`flex flex-col items-center gap-0.5 rounded-sm px-3 py-1.5 text-[10px] font-semibold tracking-wide transition-colors ${
                active
                  ? "text-[#1C4532]"
                  : "text-[#94A3B8] hover:text-[#646964]"
              }`}
            >
              <span
                className={`relative grid h-9 w-9 place-items-center rounded-full transition-colors ${
                  active ? "bg-[#1C4532]/10 text-[#1C4532]" : "text-current"
                }`}
              >
                <Icon size={20} strokeWidth={active ? 2.5 : 1.5} className={t.badge ? "animate-[bell-ring_1s_ease-in-out_infinite]" : ""} />
                {t.badge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold leading-none shadow-[0_0_0_1.5px_#FAF9F6]">
                    {t.badge > 99 ? "99+" : t.badge}
                  </span>
                )}
              </span>
              <span className="leading-tight">{t.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
